import { BooleanInput } from '@angular/cdk/coercion';
import {
  afterNextRender,
  booleanAttribute,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormGroupDirective, NgControl } from '@angular/forms';
import {
  LucideCheck,
  LucideChevronDown,
  LucideChevronRight,
  LucideChevronUp,
  LucideDynamicIcon,
  LucideSearch,
} from '@lucide/angular';
import { NgpSelect, NgpSelectDropdown, NgpSelectOption, NgpSelectPortal } from 'ng-primitives/select';
import { ChangeFn, provideValueAccessor, TouchedFn } from 'ng-primitives/utils';
import { InlineButtonComponent } from '../../atoms/inline-button/inline-button.component';
import { LoaderComponent } from '../../atoms/loader/loader.component';
import { SelectPortalContainerService } from '../../utils/select-portal-container/select-portal-container.service';
import { DialogOverlayContainer } from '../dialog/utils/overlay-container/dialog-overlay-container';

export type DropdownSize = 'sm' | 'md' | 'lg';
export interface DropdownOption<T = any> {
  name: string;
  value: T;
}

@Component({
  selector: 'joii-dropdown',
  imports: [
    NgpSelect,
    NgpSelectDropdown,
    NgpSelectOption,
    NgpSelectPortal,
    InlineButtonComponent,
    LucideDynamicIcon,
    LoaderComponent,
  ],
  providers: [provideValueAccessor(DropdownComponent)],
  template: `
    <button
      class="dropdown-trigger"
      joii-inline-button
      [fullWidth]="fullWidth()"
      variant="secondary"
      [size]="size()"
      [suffixIcon]="opened() ? chevronUpIcon : chevronDownIcon"
      [active]="opened()"
      [(ngpSelectValue)]="value"
      [ngpSelectDisabled]="disabled() || formDisabled()"
      [ngpSelectDropdownContainer]="dropdownContainer() ?? null"
      [ngpSelectScrollToOption]="scrollToOption"
      [attr.autocomplete]="autocomplete() || null"
      [attr.aria-invalid]="hasError"
      (ngpSelectValueChange)="onValueChange($event)"
      (ngpSelectOpenChange)="setOpenState($event)"
      ngpSelect
      type="button"
    >
      @if (value(); as selectedOption) {
        <span class="select-value">{{ selectedOption.name }}</span>
      } @else {
        <span class="select-placeholder">{{ placeholder() }}</span>
      }
      <ng-template ngpSelectPortal>
        <div
          #dropdownPanel
          ngpSelectDropdown
          class="joii-dropdown-panel"
          (click)="$event.stopPropagation()"
          (wheel)="$event.stopPropagation()"
          (touchmove)="$event.stopPropagation()"
        >
          @if (searchable()) {
            <div class="dropdown-search-container">
              <div class="dropdown-search-input-wrapper">
                <svg [lucideIcon]="searchIcon" class="dropdown-search-icon" [strokeWidth]="2" />
                <input
                  type="text"
                  class="dropdown-search-input"
                  [value]="searchText()"
                  (input)="onSearchChange($any($event.target).value)"
                  (click)="$event.stopPropagation()"
                  placeholder="Search..."
                  autocomplete="off"
                />
              </div>
            </div>
          }
          @if (loading()) {
            <div class="dropdown-loading">
              <joii-loader [size]="24" ariaLabel="Loading options" />
            </div>
          } @else {
            @for (option of filteredOptions(); track trackOption(option)) {
              <button
                #dropdownOption
                [ngpSelectOptionValue]="option"
                ngpSelectOption
                joii-inline-button
                variant="secondary"
                [suffixIcon]="option.value === value()?.value ? checkIcon : chevronRightIcon"
                [size]="size()"
                [active]="option.value === value()?.value"
                [fullWidth]="true"
                type="button"
              >
                <span>{{ option.name }}</span>
              </button>
            } @empty {
              <button joii-inline-button class="empty-message" [size]="size()" variant="secondary" [fullWidth]="true">
                No options found
              </button>
            }
          }
        </div>
      </ng-template>
    </button>
  `,
  host: {
    '[style.--joii-dropdown-width]': "width() + 'px'",
    '[attr.data-joii-dropdown-width]': 'width()',
    '[attr.data-full-width]': 'fullWidth()',
    '[attr.data-error]': 'hasError ? "true" : null',
  },
  styleUrl: './dropdown.component.css',
})
export class DropdownComponent implements ControlValueAccessor {
  /** The options for the select. */
  readonly options = input<DropdownOption[]>([]);

  /** The selected value. */
  readonly value = model<DropdownOption<any> | undefined>();

  /** The placeholder for the input. */
  readonly placeholder = input<string>('');
  /** The size of the dropdown. */
  readonly size = input<DropdownSize>('md');

  /** Whether to set the width to 100% of the parent container. */
  readonly fullWidth = input<boolean>(false);

  readonly width = input<number>();

  /** The disabled state of the select. */
  readonly disabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  /** Whether options are currently loading. */
  readonly loading = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  private readonly injector = inject(Injector);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formDirective = inject(FormGroupDirective, { optional: true });
  private readonly selectPortalContainer = inject(SelectPortalContainerService);
  private readonly dialogOverlayContainer = inject(DialogOverlayContainer, { optional: true });
  private readonly dropdownPanel = viewChild<ElementRef<HTMLElement>>('dropdownPanel');
  private readonly dropdownOptions = viewChildren<ElementRef<HTMLElement>>('dropdownOption');

  /** Centre the active option on the scroll caused by opening, not on keyboard navigation. */
  private centreActiveOption = false;

  /** Signal that updates when the attached control's validation status or value changes. Resolved lazily to avoid NG0200 circular DI. */
  private readonly controlStatusVersion = signal(0);

  /**
   * Raw value written by the form control, held so it can resolve once options load.
   * `null` means no CVA write has occurred (plain `[value]` usages stay untouched).
   */
  private readonly controlValue = signal<{ raw: unknown } | null>(null);

  constructor() {
    afterNextRender(() => {
      const control = this.injector.get(NgControl, null, { self: true, optional: true })?.control;
      if (!control) return;
      const bumpVersion = () => this.controlStatusVersion.update((v) => v + 1);
      control.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(bumpVersion);
      control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(bumpVersion);
      this.formDirective?.ngSubmit.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(bumpVersion);
    });

    effect(() => {
      const stored = this.controlValue();
      if (stored === null) {
        return;
      }

      const raw = stored.raw;
      if (raw === null || raw === undefined) {
        this.value.set(undefined);
        return;
      }

      const options = this.options();
      const option = options.find((opt) => opt.value === raw);
      if (option) {
        this.value.set(option);
      } else if (options.length > 0) {
        // Options loaded but the raw value is not among them.
        this.value.set(undefined);
      }
      // else: options have not loaded yet — wait rather than clearing the selection
    });
  }

  /** Effective container: nested overlay (e.g. calendar input), then dialog overlay container. */
  protected readonly dropdownContainer = computed(
    () => this.selectPortalContainer.currentContainer() ?? this.dialogOverlayContainer?.currentContainer() ?? null
  );

  /** The autocomplete attribute value. */
  readonly autocomplete = input<string>('');

  /** Whether the dropdown is searchable. */
  readonly searchable = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly opened = signal<boolean>(false);

  /** Store the form disabled state */
  protected readonly formDisabled = signal(false);

  /** Search text for filtering options */
  protected readonly searchText = signal<string>('');

  /** Filtered options based on search text */
  protected readonly filteredOptions = computed<DropdownOption[]>(() => {
    if (!this.searchable()) {
      return this.options();
    }
    const search = this.searchText().toLowerCase().trim();
    if (!search) {
      return this.options();
    }
    return this.options().filter((option) => option.name.toLowerCase().includes(search));
  });

  /** True when this dropdown's own FormControl is invalid and (touched or submitted). */
  get hasError(): boolean {
    this.controlStatusVersion();
    const control = this.injector.get(NgControl, null, { self: true, optional: true })?.control;
    const submitted = this.formDirective?.submitted ?? false;
    const showError = control?.touched || submitted;
    return !!(control?.invalid && showError);
  }

  chevronDownIcon = LucideChevronDown;
  chevronUpIcon = LucideChevronUp;
  chevronRightIcon = LucideChevronRight;
  checkIcon = LucideCheck;
  searchIcon = LucideSearch;

  /** The on change callback */
  private onChange?: ChangeFn<any>;

  /** The on touch callback */
  protected onTouched?: TouchedFn;

  writeValue(value: any): void {
    this.controlValue.set({ raw: value });
  }

  registerOnChange(fn: ChangeFn<any>): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: TouchedFn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }

  setOpenState(isOpen: boolean) {
    this.opened.set(isOpen);
    if (!isOpen) {
      this.searchText.set('');
      this.onTouched?.();
      return;
    }
    this.centreActiveOption = true;
    // ngpSelect matches its selected option by reference, so it skips the scroll
    // when the value is an equivalent but distinct object. Defer past the panel
    // being rendered and centre it ourselves in that case.
    setTimeout(() => {
      if (this.centreActiveOption) {
        this.scrollSelectedOptionIntoView();
      }
    }, 0);
  }

  /**
   * Replaces ngpSelect's own scrolling, which aligns the option to the nearest
   * panel edge. Centring only applies to the scroll caused by opening: keyboard
   * navigation keeps the minimal scroll so the list doesn't jump on every key.
   */
  protected readonly scrollToOption = (index: number): void => {
    const panel = this.panelElement();
    const optionEl = this.optionElement(panel, index);
    if (!panel || !optionEl) {
      return;
    }

    if (this.centreActiveOption) {
      this.centreActiveOption = false;
      this.centreOption(panel, optionEl);
      return;
    }
    this.revealOption(panel, optionEl);
  };

  private scrollSelectedOptionIntoView(): void {
    const selectedValue = this.value()?.value;
    if (selectedValue === undefined) {
      return;
    }

    const selectedIndex = this.filteredOptions().findIndex((option) => option.value === selectedValue);
    if (selectedIndex < 0) {
      return;
    }

    const panel = this.panelElement();
    const optionEl = this.optionElement(panel, selectedIndex);
    if (!panel || !optionEl) {
      return;
    }

    this.centreActiveOption = false;
    this.centreOption(panel, optionEl);
  }

  /**
   * Centres the option in the panel, leaving options visible above and below it.
   * Clamped to the scroll range, so options near either end sit as close to the
   * centre as the list allows.
   */
  private centreOption(panel: HTMLElement, optionEl: HTMLElement): void {
    const centredScrollTop = optionEl.offsetTop - (panel.clientHeight - optionEl.offsetHeight) / 2;
    const maxScrollTop = Math.max(0, panel.scrollHeight - panel.clientHeight);
    panel.scrollTop = Math.max(0, Math.min(centredScrollTop, maxScrollTop));
  }

  /** Scrolls the minimum amount needed to bring the option into view. */
  private revealOption(panel: HTMLElement, optionEl: HTMLElement): void {
    const optionTop = optionEl.offsetTop;
    const optionBottom = optionTop + optionEl.offsetHeight;
    if (optionTop < panel.scrollTop) {
      panel.scrollTop = optionTop;
    } else if (optionBottom > panel.scrollTop + panel.clientHeight) {
      panel.scrollTop = optionBottom - panel.clientHeight;
    }
  }

  // The panel and its options are rendered through ngpSelectPortal, outside this
  // component's view, so the view queries can miss them.
  private panelElement(): HTMLElement | null {
    return this.dropdownPanel()?.nativeElement ?? document.querySelector<HTMLElement>('.joii-dropdown-panel');
  }

  private optionElement(panel: HTMLElement | null, index: number): HTMLElement | null {
    return (
      this.dropdownOptions()[index]?.nativeElement ??
      panel?.querySelectorAll<HTMLElement>('[ngpSelectOption]').item(index) ??
      null
    );
  }

  protected onSearchChange(value: string): void {
    this.searchText.set(value);
    // The panel is a portal view that ngpSelect first renders on its own, so the signal change alone doesn't
    // redraw it inside an OnPush host. Marking this view refreshes the panel along with it.
    this.changeDetectorRef.markForCheck();
  }

  protected trackOption(option: DropdownOption): unknown {
    const value = option.value;
    if (value && typeof value === 'object' && 'unique_identifier' in value) {
      return (value as { unique_identifier: string }).unique_identifier;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }
    return option.name;
  }

  protected onValueChange(option: DropdownOption | undefined): void {
    this.value.set(option);
    const valueToEmit = option?.value;
    if (this.controlValue() !== null || this.onChange) {
      this.controlValue.set({ raw: valueToEmit });
    }
    // Defer so form control update runs after dropdown close (avoids first selection not applying in modal)
    setTimeout(() => {
      this.onChange?.(valueToEmit);
      this.onTouched?.();
    }, 0);
  }
}
